import { Box, useTheme } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Pomery pinu sú odmerané z nahrávky Bolt appky (iPhone, 3 px na bod): hlava
 * 48 bodov, biela bodka v nej 16, nožička 19 dlhá a 3,3 hrubá. V pôvodnej
 * veľkosti bol nad našou mapou priveľký, tak je zmenšený na 2/3 (najprv na
 * 5/6, aj to bolo veľa). Hlava je potom ešte o polovicu menšia než pomer
 * Boltu: s hrubým čiernym okrajom je z nej guľôčka špendlíka a ihla je
 * proti nej dlhšia, ako pri skutočnom špendlíku.
 */
const HEAD = 16;
const DOT = 5;
const STEM = 13;
/** Celé číslo zámerne: ihla tak stojí na celých pixeloch a nerozmaže sa. */
const STEM_WIDTH = 2;
const HEIGHT = HEAD + STEM;

/**
 * Ihla nie je zaoblená tyčka ako v Bolte, ale končí hrotom: posledných
 * `NEEDLE_TIP` pixelov sa zúži do bodu, ako skutočný špendlík. Hrot je
 * zároveň presne `view.center`.
 */
const NEEDLE_TIP = 5;

/**
 * Čierny okraj okolo hlavy (`grey[900]`, ako ihla). Bolt ho nemá, ale jeho
 * tmavý pin stojí na svetlej mape sám; náš je červený ako nové podnety a bez
 * okraja sa medzi ne nehodil. Biely okraj bol prvý pokus - čierny s čiernou
 * ihlou robí z pinu jeden čierno-červený tvar, ktorý si so značkou podnetu
 * nikto nezamení. Ihla vlastný okraj nemá: hrot by bol tupý.
 */
const BORDER = 3;

/**
 * Mapa stojí, keď sa jej stred `SETTLE_MS` neposunul o viac než `SETTLE_PX`
 * na obrazovke - vtedy pin dopadne (viď `settleLater`).
 */
const SETTLE_PX = 1;
const SETTLE_MS = 90;

/** O koľko sa pin pri pohybe mapy zdvihne nad svoj bod (v Bolte 20 bodov). */
const LIFT = 13;

/**
 * Sploštenie pri dopade. Hlava aj nožička sa stlačia od hrotu nahor a šírka
 * zostane - na snímke je hlava pri dopade 48 × 39, nožička o pätinu kratšia.
 */
const SQUASH = 0.82;

type Phase = 'rest' | 'lifted' | 'landed';

type Props = {
  /** Pohľad mapy - pin sa zdvíha, kým sa mapa hýbe (`view.stationary`). */
  view: __esri.MapView;
  /** Pravý okraj pohľadu mapy (`view.padding.right`), teda šírka panela. */
  right?: number;
  /** Spodný okraj pohľadu mapy (`view.padding.bottom`), teda zatvorený list. */
  bottom?: number;
  /** Feed sa doťahuje - bodka v hlave pulzuje, ako keď Bolt hľadá adresu. */
  loading?: boolean;
};

/**
 * Pevný pin v strede mapy (moj-presov#33), skopírovaný z Boltu: mapa sa posúva
 * pod ním a pin hovorí, kam sa používateľ pozerá a od ktorého bodu sa počíta
 * zoradenie „od najbližších".
 *
 * **Pohyb mapy pin zdvihne.** Kým mapa nestojí, vznáša sa hlava s nožičkou
 * `LIFT` nad bodom a na samotnom bode zostane tieň - je vidieť, kam pin
 * dopadne. Keď sa mapa zastaví (aj po dobehnutí zotrvačnosti), pin spadne
 * a pri dopade sa sploští (`SQUASH`), čo mu dáva tú váhu. Zdvíha ho
 * `view.stationary`, nie len ťahanie prstom: let na nájdené miesto či na
 * polohu zariadenia je tiež pohyb.
 *
 * **Stred je stred voľnej plochy, nie celej mapy** - na desktope je vpravo
 * panel, na mobile dole list. Pin sa preto odsádza tými istými číslami, aké
 * dostal pohľad do `view.padding`, takže hrot stojí presne na `view.center`.
 *
 * Kliknutia neberie - je to značka miesta, nie ovládací prvok, a značky feedu
 * pod ním musia ostať klikateľné.
 */
const MapCenterPin: React.FC<Props> = ({
  view,
  right = 0,
  bottom = 0,
  loading = false,
}) => {
  const { palette } = useTheme();
  const [phase, setPhase] = React.useState<Phase>('rest');

  React.useEffect(() => {
    let isCancelled = false;
    const handles: { remove: () => void }[] = [];

    // `landed` len po zdvihnutí: pin, ktorý sa ukáže (zatvorenie detailu),
    // nemá odkiaľ padať.
    const land = () =>
      setPhase((current) => (current === 'lifted' ? 'landed' : current));

    /**
     * Pin dopadá, keď sa mapa **viditeľne** zastaví, nie až na `stationary`.
     *
     * Pustenie prsta to nie je - mapa po ňom ešte dobieha zotrvačnosťou a pin
     * zapichnutý do bežiacej mapy vyzeral, akoby sa s ňou vliekol. Samotné
     * `stationary` je zase neskoro: zotrvačnosť končí dlhým chvostom posunov
     * pod pixel, ktoré oko nevidí, a pin visel ešte chvíľu nad stojacou
     * mapou. Preto sa sleduje stred na obrazovke: keď sa `SETTLE_MS` nepohol
     * o viac než `SETTLE_PX`, mapa stojí. Kým drží prst, nedopadne ani na
     * mieste - používateľ ešte mieri.
     */
    let isDragging = false;
    let lastCenter: { x: number; y: number } | undefined;
    let settleTimer: number | undefined;

    const settleLater = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (!isDragging) land();
      }, SETTLE_MS);
    };

    import('@arcgis/core/core/reactiveUtils').then(({ watch }) => {
      if (isCancelled) return;
      handles.push(
        watch(
          () => view.stationary,
          (stationary) => {
            if (!stationary) {
              lastCenter = undefined;
              setPhase('lifted');
              return;
            }
            // Poistka: zoom bez posunu stred nemení, takže dopadne až tu.
            window.clearTimeout(settleTimer);
            land();
          },
        ),
        watch(
          () => view.center,
          (center) => {
            if (!center || view.stationary) return;
            const moved = lastCenter
              ? Math.hypot(center.x - lastCenter.x, center.y - lastCenter.y) /
                view.resolution
              : Infinity;
            if (moved < SETTLE_PX) return;
            lastCenter = { x: center.x, y: center.y };
            settleLater();
          },
        ),
        view.on('drag', ({ action }) => {
          if (action === 'start') isDragging = true;
          if (action === 'end') {
            isDragging = false;
            // Pustenie bez zotrvačnosti už žiadny posun neprinesie, tak by
            // časovač nemal kto spustiť.
            settleLater();
          }
        }),
      );
    });

    return () => {
      isCancelled = true;
      window.clearTimeout(settleTimer);
      handles.forEach((handle) => handle.remove());
    };
  }, [view]);

  return (
    <Box
      aria-hidden
      sx={{
        // Bod nulovej veľkosti presne na `view.center`; pin aj tieň sa
        // kreslia okolo neho.
        //
        // Zaokrúhlené na celý pixel: polovica nepárnej šírky mapy padne do
        // pol pixela a prehliadač potom tenkú ihlu rozmaže - na každej strane
        // inak, takže hrot vyzeral ostrý len z jednej strany a celý pin
        // rozmazaný. O pol pixela vedľa `view.center` nikto nepozná.
        position: 'absolute',
        left: `round(calc((100% - ${right}px) / 2), 1px)`,
        top: `round(calc((100% - ${bottom}px) / 2), 1px)`,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        // Nad mapou, pod čiarou k značke (900) aj pod hľadaním (1000). S čiarou
        // sa v skutočnosti nestretne - pri otvorenom detaile je pin skrytý.
        zIndex: 800,
        // Objaví sa, keď je mapa nakreslená (a po zatvorení detailu) -
        // jemne, aby nevyskočil.
        animation: 'mapCenterPinIn .2s ease-out',
        '@keyframes mapCenterPinIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      {/* Tieň na bode, kam pin dopadne. Vidieť je len kým sa pin vznáša -
          v pokoji ho prekrýva hrot. */}
      <Box
        sx={{
          position: 'absolute',
          left: -3,
          top: -2,
          width: 6,
          height: 4,
          borderRadius: '50%',
          backgroundColor: 'rgba(23, 21, 18, 0.45)',
          filter: 'blur(0.5px)',
          opacity: phase === 'lifted' ? 1 : 0,
          transition: 'opacity .15s ease-out',
        }}
      />

      <Box
        component='svg'
        viewBox={`0 0 ${HEAD} ${HEIGHT}`}
        sx={(theme) => ({
          position: 'absolute',
          left: -HEAD / 2,
          top: -HEIGHT,
          width: HEAD,
          height: HEIGHT,
          overflow: 'visible',
          color: theme.palette.primary.main,
          // Sploštenie sa ťahá od hrotu - ten stojí na bode a nehýbe sa.
          transformOrigin: '50% 100%',
          transform: phase === 'lifted' ? `translateY(-${LIFT}px)` : 'none',
          transition: 'transform .16s ease-out',
          filter: 'drop-shadow(0 2px 4px rgba(23, 21, 18, 0.25))',
          animation:
            phase === 'landed' ? 'mapCenterPinDrop .36s ease-out' : 'none',
          // Pád trvá zlomok, väčšinu času zaberie sploštenie a návrat, ako
          // na nahrávke: o snímku na zemi, dve snímky stlačený, potom guľatý.
          '@keyframes mapCenterPinDrop': {
            '0%': {
              transform: `translateY(-${LIFT}px)`,
              animationTimingFunction: 'ease-in',
            },
            '30%': { transform: 'translateY(0) scaleY(1)' },
            '55%': { transform: `translateY(0) scaleY(${SQUASH})` },
            '100%': { transform: 'translateY(0) scaleY(1)' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            animation: 'none',
          },
        })}
      >
        {/* Okraj hlavy pod ňou, v tej istej čiernej ako ihla - ihla tak
            z okraja plynule vychádza. */}
        <circle
          cx={HEAD / 2}
          cy={HEAD / 2}
          r={HEAD / 2 + BORDER}
          fill={palette.grey[900]}
        />
        {/* Ihla: rovná až po `NEEDLE_TIP` nad bodom, potom zúžená do hrotu.
            Začína pod hlavou, aby medzi nimi nezostala škára. */}
        <path
          d={`M${(HEAD - STEM_WIDTH) / 2},${HEAD - 2} h${STEM_WIDTH} V${
            HEIGHT - NEEDLE_TIP
          } L${HEAD / 2},${HEIGHT} L${(HEAD - STEM_WIDTH) / 2},${
            HEIGHT - NEEDLE_TIP
          } Z`}
          fill={palette.grey[900]}
        />
        <circle cx={HEAD / 2} cy={HEAD / 2} r={HEAD / 2} fill='currentColor' />
        <Box
          component='circle'
          cx={HEAD / 2}
          cy={HEAD / 2}
          r={DOT / 2}
          sx={{
            // Biely stred ako v Bolte. Čierny bol skúšaný a nevyšiel.
            fill: palette.common.white,
            // Bolt bodku pri hľadaní adresy prefarbí do farby hlavy a späť.
            // Biela s nižšou krycou silou nad červenou spraví to isté bez
            // druhej farby.
            animation: loading
              ? 'mapCenterPinPulse .9s ease-in-out infinite'
              : 'none',
            '@keyframes mapCenterPinPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.45 },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default MapCenterPin;
